/**
 * @fileoverview Order status field component for status management and workflow controls.
 * Provides interactive status updates, problem tagging, confirmation dialogs, and
 * real-time status broadcasting for collaborative order management.
 *
 * @author Web Analysis Team
 * @version 1.0.0
 */

/* eslint-disable no-unused-vars */
/* eslint-disable eqeqeq */
import { Alert, Box, Button, ButtonGroup, Chip, Divider, List, ListItemButton, ListItemText, OutlinedInput, Paper, Popover, Stack, TextField, Tooltip, Typography } from '@mui/material';
import React from 'react';
import { Confirm, useNotify, useRecordContext } from 'react-admin';
import { STATUS_ENUM, STATUS_NAME, getTagsColor, ProblemList } from '../../../../utils/enum';
import { Send, Mail, CloudUpload, Done, Clear, Check } from '@mui/icons-material';

interface IData {
    [key: string]: any;
}

const StatusField: React.FC<IData> = (props) => {
    const { mydayjs, currUser, dataProvider, appIO, setProcessingState, setEffectData } = props;
    const { vendor, bus, bus_events } = appIO;

    const record = useRecordContext();
    if (!record || record.id === 0) return;

    const notify = useNotify();
    const [confirmStateUpPs, setConfirmStateUpPs] = React.useState<IData>({ open: false, message: '' });
    const [confirmStateUc, setConfirmStateUc] = React.useState<IData>({ open: false, rejectReason: '' });//unconfirm
    const [problemState, setProblemState] = React.useState<IData>({ open: false, currentTarget: undefined });
    const [seatNumbers, setSeatNumbers] = React.useState('');

    const orderTags = record?.tags?.split(',')?.filter((x: any) => x) || [];

    let isHardTicket = record?.delivery_method == 1;
    let airBill = isHardTicket && record.airBill && record.airBill == true;

    const KEYS_TAG_CHANGED_ORDER = ['order_number', 'ID', 'vivid_account_id', 'processor', 'shipper', 'shipping', 'unit_list_price',
        'order_total', 'section', 'quantity', 'row', 'tags', 'order_notes', 'order_fullnotes', 'internal_status', 'utc_last_updated_on'];

    function broadcastChangedOrder (order, keys) {

        let changedOrder = [];

        for (let key in order) {

            if (keys.indexOf(key) >= 0) {
                if (key == 'order_notes') {
                    let notes = order[key];

                    changedOrder.push({ name: 'order_notes', value: notes.reduce(function (prev: any, cur: any) { if (prev.length < 3 && cur.note_type == 0) prev.push(cur); return prev; }, []) });
                    changedOrder.push({ name: 'order_fullnotes', value: notes });

                    continue;
                }

                changedOrder.push({ name: key, value: order[key] });
            }
        }

        return bus.broadcast(bus_events.order_saved, changedOrder);
    }

    const handleTagDelete = (props, tag) => {
        let currTags = record.tags?.split(',')?.filter((x: any) => x);
        let newTags = currTags?.filter((x: any) => x != tag);
        record.tags = ',' + newTags.join(',') + ',';

        dataProvider.postData(vendor.controller, 'Update', {
            ID: record.id,
            tags: record.tags,
            updatingKeys: ['tags']
        }).then((response: any) => {
            if (response.data?.success) {
                return broadcastChangedOrder(response.data.order, KEYS_TAG_CHANGED_ORDER);
            }
        }).finally(() => setProcessingState(prev => ({ ...prev, reloadState: !prev.reloadState })));
    };

    //#region Unconfirm Order
    const clickConfirmOrder = () => {
        let order_number = record.order_number;
        dataProvider.postData(vendor.controller, `Confirm?orderNumber=${order_number}&seatNumbers=${seatNumbers}`)
            .then((response: any) => {
                if (response?.data) {
                    if (response?.data?.success) {
                        updateStatus({
                            orderNumber: order_number,
                            currentStatus: STATUS_ENUM.Unconfirmed,
                            newStatus: STATUS_ENUM.Unprocessed
                        });
                        notify(`Confirm order #${order_number} success`, { type: 'success', autoHideDuration: 3000 });
                        setProcessingState(prev => ({ ...prev, reloadState: !prev.reloadState }));
                    } else {
                        notify(`Confirm failed: order #${order_number} !!!`, { type: 'warning', autoHideDuration: 3000 });
                    }
                }
                else {
                    notify(`ERROR: ${response?.message}`, { type: 'error', autoHideDuration: 3000 });
                }
            })
            .catch((err: any) => notify(`ERROR: ${err?.message}`, { type: 'error', autoHideDuration: 3000 }));
    };
    const clickRejectOrder = () => {
        setConfirmStateUc(prev => ({ ...prev, open: true }));

    };
    const handleRejectOrder = () => {
        setConfirmStateUc(prev => ({ ...prev, open: false }));

        let order_number = record.order_number;
        dataProvider.postData(vendor.controller, `Reject?orderNumber=${order_number}`)
            .then((response: any) => {
                if (response?.data) {
                    if (response?.data?.success) {
                        updateStatus({
                            orderNumber: order_number,
                            currentStatus: STATUS_ENUM.Unconfirmed,
                            newStatus: STATUS_ENUM.Canceled,
                            problem: confirmStateUc.rejectReason
                        });
                        setConfirmStateUc(prev => ({ ...prev, rejectReason: '' }));
                        notify(`Reject order #${order_number} success`, { type: 'success', autoHideDuration: 3000 });
                        setProcessingState(prev => ({ ...prev, reloadState: !prev.reloadState }));
                    } else {
                        notify(`Reject Failed: order #${order_number} !!!`, { type: 'warning', autoHideDuration: 3000 });
                    }
                } else {
                    notify(`ERROR: ${response?.message}`, { type: 'error', autoHideDuration: 3000 });
                }
            })
            .catch((err: any) => notify(`ERROR: ${err?.message}`, { type: 'error', autoHideDuration: 3000 }));
    };
    //#endregion

    //#region UnProcess + Shipping
    function isProblemExist () {
        let problem = record.tags.split(',').filter((x: any) => x).find((x: any) => x == 'Problem');
        return !!problem;
    }

    let currentStatus = record.internal_status;
    let currentOrderNumber = record.order_number;

    const handleconfirmUpPs = () => {
        setConfirmStateUpPs(prev => ({ ...prev, open: false, message: '' }));
        if (typeof currentStatus === 'undefined' || currentStatus === null) {
            return;
        }
        changeStatus();
    };
    //Update Internal_status State => re-render
    function updateStatus (options) {
        // console.log(`updateStatus`, options, record);
        options.shipping = options.shipping || record.shipping;
        // Set status for fast render
        record.currentStatus = record.internal_status;
        record.internal_status = options.newStatus;
        record.allowRevertStatus = true;

        return bus.emitAsync(bus_events.order_status_changing, options)
            .then((result: any) => {
                if (result && result.length > 0 && result[0].status !== 'Accepted') {
                    record.internal_status = record.currentStatus;
                    notify(
                        <Alert severity="error">
                            <span style={{ fontWeight: 'bold' }}>Your command is rejected.</span><br />
                            {result[0].reason}<br />
                        </Alert>
                    );
                }
                else {
                    dataProvider.fetchData(vendor.controller, 'Get?isWithFullNotes=true&orderNumber=' + options.orderNumber)
                        .then((res: any) => {
                            let response = res.data;
                            if (response) {
                                record.utc_last_updated_on = response.utc_last_updated_on;
                                record.internal_status = response.internal_status;

                                let notes = response.order_notes;
                                record.order_notes = notes.reduce(function (prev: any, cur: any) { if (prev.length < 3 && cur.note_type == 0) prev.push(cur); return prev; }, []);
                                record.order_fullnotes = notes;
                                setEffectData((prev) => ({ ...prev, notes: notes }));
                                bus.broadcast(bus_events.order_saved, record);
                            }
                            else {
                                record.internal_status = record.currentStatus;
                                notify('Get Order failed', { type: 'error' });
                            }
                        });
                }
            });
    }

    function changeStatus () {
        let newStatus;
        switch (currentStatus) {
            case STATUS_ENUM.Unprocessed:
                newStatus = STATUS_ENUM.Processing;
                break;
            case STATUS_ENUM.PendingShipment:
                newStatus = STATUS_ENUM.Shipping;
                break;
            default:
                return;
        }
        updateStatus({
            orderNumber: currentOrderNumber,
            currentStatus: currentStatus,
            newStatus: newStatus,
        }).then(function () {
            currentStatus = record.internal_status;
            currentOrderNumber = record.order_number;
            setProcessingState(prev => ({ ...prev, reloadState: !prev.reloadState }));
        });
    }
    function confirmIfAny (message, isShipping = false) {
        if (isProblemExist()) {
            setConfirmStateUpPs(prev => ({ ...prev, open: true, message: message }));
        }
        else {
            if (isShipping) {
                record.shipper = currUser;
                changeStatus();
            } else {
                let orderNumber = currentOrderNumber;
                Promise.race([
                    fetch('https://ipinfo.io/ip').then((res: any) => res.status == 200 ? res.text() : ''),
                    fetch('https://icanhazip.com/').then((res: any) => res.status == 200 ? res.text() : ''),
                    fetch('https://api.ipify.org?format=json').then((res: any) => res.status == 200 ? res.json() : '')
                ])
                    .then((info: any) => {
                        let post = `Clicked process button, IP: ${info.ip ? info.ip : info}`;
                        dataProvider.postData(vendor.controller, `NoteAdminIP?order_number=${orderNumber}&post=${post}`);
                    });
                record.processor = currUser;
                changeStatus();
            }
        }
    }
    const clickProcess = (params) => {
        if (!record.info) return;
        record.allowRevertStatus = true; // Allow revert status
        confirmIfAny('There are problem in order. Would you want continue to process?');
    };
    const handlePendingShipment = (params) => {
        record.allowRevertStatus = true; // Allow revert status
        confirmIfAny('There are problem(s) in order. Would you want continue to ship?', true);
    };
    //#endregion

    //#region Processing
    //#region Problem report
    const clickProblem = (params) => {
        setProblemState(prev => ({ ...prev, open: true, currentTarget: params.currentTarget }));
    };
    const changeProblemList = (reason) => {
        setProblemState(prev => ({ ...prev, open: false, currentTarget: undefined }));
        console.log(props);
        if (reason.id === '#') {
            updateStatus({
                orderNumber: record.order_number,
                currentStatus: record.internal_status,
                newStatus: parseInt(record.internal_status) === STATUS_ENUM.Processing ? STATUS_ENUM.Unprocessed : STATUS_ENUM.PendingShipment
            });
        }

        let newTags = orderTags.filter((tag: any) => tag != 'Problem Resolved');//cause have problem => remove "Problem resolved"
        if (!newTags.some(tag => tag == 'Problem')) {
            newTags.push('Problem');
        }
        if (!newTags.some(tag => tag == reason.name)) {//if not exist => add
            newTags.push(reason.name);
        }
        record.tags = ',' + newTags.join(',') + ',';

        dataProvider.postData(vendor.controller, 'Update', {
            ID: record.id,
            tags: record.tags,
            updatingKeys: ['tags']
        }).then((response: any) => {
            if (response.data?.success) {
                notify('Updated !!!', { type: 'success' });
                return broadcastChangedOrder(response.data.order, KEYS_TAG_CHANGED_ORDER);
            }
        }).finally(() => setProcessingState(prev => ({ ...prev, reloadState: !prev.reloadState })));
    };
    //#endregion
    //#region DONE
    const clickDone = () => {
        let updatingKeys = ['primary_order_number', 'primary_account', 'primary_cost', 'pelnalty_cost'];
        function doUpdate (options) {
            let order = {
                order_number: record.order_number,
                ID: record.id,
                primary_order_number: record.primary_order_number,
                primary_account: record.primary_account,
                primary_cost: record.primary_cost,
                penalty_cost: record.penalty_cost,
                internal_status: validShipment() ? STATUS_ENUM.PendingShipment : STATUS_ENUM.Unprocessed,
                updatingKeys: updatingKeys.concat(['internal_status'])
            };
            for (let k in options) order[k] = options[k];

            dataProvider.postData(vendor.controller, 'Update', order)
                .then((response: any) => {
                    if (response.data) {
                        return broadcastChangedOrder(response.data.order, KEYS_TAG_CHANGED_ORDER.concat(updatingKeys));
                    }
                })
                .finally(() => setProcessingState(prev => ({ ...prev, reloadState: !prev.reloadState })));

            function validShipment () {
                return !!record.primary_order_number || !!record.primary_account || !!record.primary_cost || !!record.penalty_cost;
            }
        }

        let needUpdate = false;
        let tagLabels = orderTags.map((tag: any, index: any) => {
            if (tag == 'Problem') {
                needUpdate = true;
                return 'Problem Resolved';
            }

            return tag;
        }).join(',');

        if (needUpdate) {
            doUpdate({ tags: tagLabels });
        }
        else {
            doUpdate();
        }
    };
    //#endregion
    //#endregion

    //#region component
    const StatusTitle = (props: any) => {
        const { statusText, subStatus } = props;
        const shipping = record.shipping;
        return (<Tooltip title={`${statusText} ${subStatus || ''} ${shipping || ''}`}>
            <span>
                <Typography>{statusText}</Typography>
                <Typography sx={{ fontWeight: 'bold' }}>{subStatus || ''}</Typography>
                <Typography sx={{ overflow: 'hidden', whiteSpace: 'pre', fontWeight: 'bold' }}>{shipping || ''}</Typography>
            </span>
        </Tooltip>);
    };

    const ProblemDialog = (props: any) => {
        // console.log('Show Proplem Popup');
        return (
            <Popover
                id={'propblem-popover'}
                open={problemState.open}
                onClose={() => setProblemState(prev => ({ ...prev, open: false, currentTarget: undefined }))}
                anchorEl={problemState.currentTarget}
                anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
                transformOrigin={{ vertical: 'top', horizontal: 'center' }}
            >
                <Paper sx={{ width: '220px' }}>
                    {ProblemList.map((option, index) => (
                        <Box key={`list-${record.order_number}-${index}`}>
                            <List component="nav" sx={{ paddingTop: '0px', paddingBottom: '0px' }} >
                                <ListItemButton
                                    sx={{ paddingTop: '0px', paddingBottom: '0px' }}
                                    onClick={() => changeProblemList(option)}
                                >
                                    <ListItemText primary={
                                        <Typography variant="caption" style={{ fontSize: '13px' }}>
                                            {option.name}
                                        </Typography>} />
                                </ListItemButton>
                            </List>
                            {index == 0 && <Divider />}
                        </Box>
                    ))}
                </Paper>
            </Popover>
        );
    };
    //#endregion
    const renderStatusComp = (status) => {//uiGrid line 99
        switch (status) {
            case STATUS_ENUM.Unconfirmed:
                return <>
                    <StatusTitle statusText={'Unconfirmed'} />
                    <Stack direction='row' className="smallInput">
                        <OutlinedInput placeholder="Confirm seat#" title="confirm seat" size="small" hiddenLabel margin="none" sx={{ borderTopRightRadius: 0, borderBottomRightRadius: 0 }}
                            value={seatNumbers} onChange={(param) => setSeatNumbers(param.target.value)} type="text"
                        />
                        <ButtonGroup size="small" sx={{ fontSize: '12px' }}>
                            <Button variant="contained" size="small" color="error" id='rejectOrder' onClick={clickRejectOrder} sx={{ borderTopLeftRadius: '0px', borderBottomLeftRadius: '0px' }}><Clear /></Button>
                            <Button variant="contained" size="small" className="status-btn-success" id='confirmOrder' onClick={clickConfirmOrder}><Check /></Button>
                        </ButtonGroup>
                    </Stack>
                </>;
            case STATUS_ENUM.Canceled:
                return <><StatusTitle statusText={'Canceled'} /></>;
            case STATUS_ENUM.Unprocessed:
                return <>
                    <StatusTitle statusText={'Unprocessed'} />
                    <Button variant="contained" size="small" className="status-btn-success" onClick={clickProcess}>Process</Button>
                </>;
            case STATUS_ENUM.Processing:
                return <><StatusTitle statusText={'Processing'} subStatus={record.processor} />
                    {record.processor == currUser &&
                        <Stack direction='row' spacing={1} style={{ width: '100%' }}>
                            <Button variant="contained" size="small" color="error" startIcon={<Clear />} style={{ fontSize: '10px' }} onClick={clickProblem}>Prob.</Button>
                            <Button variant="contained" size="small" className="status-btn-success" startIcon={<Done />} style={{ fontSize: '10px' }}
                                disabled={!record.primary_account} onClick={clickDone}>Done</Button>
                        </Stack>
                    }
                </>;
            case STATUS_ENUM.PendingShipment:
                return <><StatusTitle statusText={'Available Unshipped'} />
                    <Button onClick={handlePendingShipment} variant="contained" size="small" className="status-btn-success" startIcon={isHardTicket ? <Mail /> : <CloudUpload />} children={'Ship'} />
                </>;
            case -1:
                return <><StatusTitle statusText={'Available Unshipped'} />
                    <Button variant="contained" size="small" className="status-btn-success" startIcon={isHardTicket ? <Mail /> : <CloudUpload />} onClick={handlePendingShipment}>Ship</Button>
                </>;
            case STATUS_ENUM.Shipping:
                return <><StatusTitle statusText={'Shipping'} subStatus={record.shipper} />
                    {record.shipper === currUser &&
                        <Stack direction='row' spacing={1} style={{ width: '100%' }}>
                            <Button variant="contained" size="small" color="error" startIcon={<Clear />} style={{ fontSize: '10px' }} onClick={clickProblem}>Prob.</Button>
                            <Button variant="contained" size="small" className="status-btn-success" startIcon={<Done />} style={{ fontSize: '10px' }}
                                disabled={!record.primary_account} onClick={clickDone}>Done</Button>
                        </Stack>
                    }
                </>;
            case STATUS_ENUM.DelayedUnshippedOrders:
                return <>
                    <StatusTitle statusText={'Delayed Unshipped'} subStatus={record.utc_delivery_date ? `Avail ${mydayjs.utc(record.utc_delivery_date).fromNow()}` : ''} />
                </>;
            case STATUS_ENUM.Completed:
                return <StatusTitle statusText={'Completed'}
                    subStatus={airBill ? <a style={{ fontSize: '12px' }} target="_blank" href={`AirBill?orderNumber=${record.order_number}`}>Get Air-bill</a> : ''} />;
            case -4:
                return <StatusTitle statusText={'Completed'}
                    subStatus={airBill ? <a style={{ fontSize: '12px' }} target="_blank" href={`AirBill?orderNumber=${record.order_number}`}>Get Air-bill</a> : ''} />;
            default:
                return <StatusTitle statusText={STATUS_NAME[status]} />;
        }
    };
    return (
        <div style={{ display: 'block', widht: '100px' }}>
            {orderTags.map((tag, index) => {
                return <Chip key={`${record.order_number}-tag-${index}`} label={tag} onDelete={(props) => handleTagDelete(props, tag)} size='small'
                    sx={{ backgroundColor: getTagsColor(tag?.toLowerCase()) }}
                />;
            })}

            {
                renderStatusComp(record.internal_status)
            }
            <Confirm isOpen={confirmStateUpPs.open}
                title='Confirm'
                content={confirmStateUpPs.message}
                onClose={() => setConfirmStateUpPs((prev) => ({ ...prev, open: false, message: '' }))}
                onConfirm={handleconfirmUpPs} />
            <Confirm isOpen={confirmStateUc.open}
                title='Reject'
                content={<TextField variant="outlined"
                    placeholder="Please input reject reason"
                    value={confirmStateUc.rejectReason}
                    onChange={(props) => setConfirmStateUc(prev => ({ ...prev, rejectReason: props.target.value }))} />}
                onClose={() => setConfirmStateUc((prev) => ({ ...prev, open: false, message: '' }))}
                onConfirm={handleRejectOrder}
                ConfirmIcon={() => <Send />}
                confirm="Send"
                confirmColor="warning" />
            <ProblemDialog />
        </div>
    );
};
export default StatusField;
